import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from "@mui/material";
import { getToken, base_url } from "../../config/config";

const TaskLogPage = () => {
  const [taskLogs, setTaskLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [newTaskLog, setNewTaskLog] = useState({
    user: "",
    type: "",
    startTime: "",
    endTime: "",
    totalTime: "",
    description: "",
    date: "",
  });

  const location = useLocation();

  // Parse query parameters
  const query = new URLSearchParams(location.search);
  const date = query.get("date");
  const employeeName = query.get("employeeName");

  useEffect(() => {
    const fetchTaskLogs = async () => {
      try {
        // Use POST request instead of GET
        const response = await axios.post(
          `${base_url}/v1/getTaskLogsByDateAndEmployee`,
          {
            date,
            employeeName,
          },
        );
        setTaskLogs(response.data);
        setLoading(false);
      } catch (error) {
        setError(error.response?.data?.error || "An error occurred");
        setLoading(false);
      }
    };

    fetchTaskLogs();
  }, [date, employeeName]);

  const handleAddOpen = () => {
    setNewTaskLog({
      ...newTaskLog,
      user: employeeName,
      date: date,
    });
    setOpenAddModal(true);
  };

  const handleAddChange = (event) => {
    const { name, value } = event.target;
    setNewTaskLog((prev) => {
      const updated = { ...prev, [name]: value };

      // Calculate total time if both startTime and endTime are present
      if (updated.startTime && updated.endTime) {
        updated.totalTime = calculateTotalTime(
          updated.startTime,
          updated.endTime,
        );
      }

      return updated;
    });
  };

  const calculateTotalTime = (startTime, endTime) => {
    const [startHour, startMinute] = startTime.split(":").map(Number);
    const [endHour, endMinute] = endTime.split(":").map(Number);

    const start = new Date();
    start.setHours(startHour, startMinute, 0, 0);

    const end = new Date();
    end.setHours(endHour, endMinute, 0, 0);

    const diff = (end - start) / 60000; // Difference in minutes
    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;

    return `${hours}h ${minutes}m`;
  };

  const handleSaveNewTaskLog = () => {
    axios
      .post(`${base_url}/v1/TaskLog`, newTaskLog)
      .then((response) => {
        setTaskLogs((prevLogs) => [...prevLogs, response.data]);
        setOpenAddModal(false);
      })
      .catch((error) => {
        console.error("There was an error creating the task log!", error);
      });
  };

  return (
    <>
      <Typography variant='h4' gutterBottom>
        Task Logs for {employeeName} on {date}
      </Typography>

      <Button
        variant='contained'
        color='primary'
        onClick={handleAddOpen}
        style={{ marginBottom: "16px" }}>
        Add Task Log
      </Button>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Start Time</TableCell>
              <TableCell>End Time</TableCell>
              <TableCell>Total Time</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5}>Loading...</TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={5} style={{ color: "red" }}>
                  {error}
                </TableCell>
              </TableRow>
            ) : taskLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>No task logs found.</TableCell>
              </TableRow>
            ) : (
              taskLogs.map((log) => (
                <TableRow key={log._id}>
                  <TableCell>{log.type}</TableCell>
                  <TableCell>{log.startTime}</TableCell>
                  <TableCell>{log.endTime}</TableCell>
                  <TableCell>{log.totalTime}</TableCell>
                  <TableCell>{log.description}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Task Log Modal */}
      <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)}>
        <DialogTitle>Add New Task Log</DialogTitle>
        <DialogContent>
          <TextField
            select
            margin='dense'
            label='Type'
            type='text'
            fullWidth
            name='type'
            value={newTaskLog.type}
            onChange={handleAddChange}>
            <MenuItem value='Erstellen Sie ein Angebot'>
              Erstellen Sie ein Angebot
            </MenuItem>
            <MenuItem value='Konstruktion / Messung'>
              Konstruktion / Messung
            </MenuItem>
            <MenuItem value='Schulung bei Herrn Wilfry Büttner'>
              Schulung bei Herrn Wilfry Büttner
            </MenuItem>
            <MenuItem value='Andere'>Andere</MenuItem>
          </TextField>
          <TextField
            margin='dense'
            label='Start Time'
            type='time'
            fullWidth
            name='startTime'
            value={newTaskLog.startTime}
            onChange={handleAddChange}
          />
          <TextField
            margin='dense'
            label='End Time'
            type='time'
            fullWidth
            name='endTime'
            value={newTaskLog.endTime}
            onChange={handleAddChange}
          />
          <TextField
            margin='dense'
            label='Total Time'
            type='text'
            fullWidth
            name='totalTime'
            value={newTaskLog.totalTime}
            disabled
          />
          <TextField
            margin='dense'
            label='Description'
            type='text'
            fullWidth
            name='description'
            value={newTaskLog.description}
            onChange={handleAddChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddModal(false)} color='secondary'>
            Cancel
          </Button>
          <Button onClick={handleSaveNewTaskLog} color='primary'>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TaskLogPage;
